import { Fragment, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, DocumentArrowUpIcon } from '@heroicons/react/24/outline'
import { Copy } from 'lucide-react'

interface DataCheckModalProps {
  isOpen: boolean
  onClose: () => void
  filters: {
    selectedCreationYears: Set<number>
    selectedArrivalYears: Set<number>
    selectedManagers: Set<string>
    selectedSegments: Set<string>
    selectedClients: Set<string>
    selectedHotels: Set<string>
    selectedHotelGroups: Set<string>
    selectedArrivalMonths: Set<number>
    selectedArrivalQuarters: Set<number>
    selectedCreationMonths: Set<number>
    selectedCreationQuarters: Set<number>
    allClientsSelected: boolean
    allHotelsSelected: boolean
    allHotelGroupsSelected: boolean
  }
  availableOptions: {
    availableCreationYears: number[]
    availableArrivalYears: number[]
    availableManagers: string[]
    availableSegments: string[]
    clients: string[]
    hotels: string[]
    hotelGroups: string[]
  }
}

interface ExcelRow {
  request_id: number
  status: string
  estimated_revenue: number
  final_revenue: number
  group_count: number
  manager?: string
  company?: string
  segment?: string
  check_in?: string
  venue?: string
  client_request_reference?: string
}

interface ComparisonResult {
  missingInView: ExcelRow[]
  missingInFile: any[]
  statusDiscrepancies: Array<{
    request_id: number
    fileStatus: string
    viewStatus: string
  }>
  groupCountDiscrepancies: Array<{
    request_id: number
    fileCount: number
    viewCount: number
  }>
  estimatedRevenueDiscrepancies: Array<{
    request_id: number
    fileRevenue: number
    viewRevenue: number
  }>
  finalRevenueDiscrepancies: Array<{
    request_id: number
    fileRevenue: number
    viewRevenue: number
  }>
  viewRecordCount: number
  totalEstimatedRevenueFile: number
  totalEstimatedRevenueView: number
  totalFinalRevenueFile: number
  totalFinalRevenueView: number
}

export function DataCheckModal({ isOpen, onClose, filters, availableOptions }: DataCheckModalProps) {
  const [excelData, setExcelData] = useState<ExcelRow[]>([])
  const [comparisonResult, setComparisonResult] = useState<ComparisonResult | null>(null)
  const [isComparing, setIsComparing] = useState(false)
  const [activeTab, setActiveTab] = useState<'filters' | 'comparison'>('filters')
  const [copySuccess, setCopySuccess] = useState<'grouped' | 'detailed' | null>(null)

  // Clear comparison data
  const clearComparison = () => {
    setExcelData([])
    setComparisonResult(null)
    setIsComparing(false)
  }

  // Copy text to clipboard
  const copyToClipboard = async (text: string, type: 'grouped' | 'detailed') => {
    try {
      await navigator.clipboard.writeText(text)
      setCopySuccess(type)
      // Clear success message after 2 seconds
      setTimeout(() => setCopySuccess(null), 2000)
    } catch (err) {
      console.error('Failed to copy text: ', err)
    }
  }

  // Helper function to format filter values
  const formatFilterValues = (selected: Set<any>, allOptions: any[], allSelected: boolean = false) => {
    if (allSelected || selected.size === 0 || selected.size === allOptions.length) {
      return 'All'
    }
    return Array.from(selected).sort().join(', ')
  }

  // Parse Excel file
  const parseExcelFile = async (file: File): Promise<ExcelRow[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer)
          
          // Import xlsx dynamically
          const XLSX = await import('xlsx')
          const workbook = XLSX.read(data, { type: 'array' })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          
          console.log('Sheet names:', workbook.SheetNames)
          console.log('Selected sheet:', sheetName)
          
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][]
          
          console.log('Raw JSON data length:', jsonData.length)
          console.log('First few rows of raw data:', jsonData.slice(0, 3))
          
          if (jsonData.length < 2) {
            throw new Error('File must contain at least a header row and one data row')
          }
          
          const headers = jsonData[0] as string[]
          const rows: ExcelRow[] = []
          
          // Debug: Log headers to see what columns are available
          console.log('Excel headers:', headers)
          
          // Find column indices - map to actual Excel headers
          let requestIdIndex = headers.findIndex(h => h === 'Id')
          if (requestIdIndex === -1) {
            requestIdIndex = headers.findIndex(h => h === 'request_id')
          }
          if (requestIdIndex === -1) {
            requestIdIndex = headers.findIndex(h => h?.toLowerCase().includes('request') && h?.toLowerCase().includes('id'))
          }
          if (requestIdIndex === -1) {
            requestIdIndex = headers.findIndex(h => h?.toLowerCase().includes('id'))
          }
          
          let statusIndex = headers.findIndex(h => h === 'Status')
          if (statusIndex === -1) {
            statusIndex = headers.findIndex(h => h === 'status')
          }
          if (statusIndex === -1) {
            statusIndex = headers.findIndex(h => h?.toLowerCase().includes('status'))
          }
          
          let estimatedRevenueIndex = headers.findIndex(h => h === 'Estimated revenue')
          if (estimatedRevenueIndex === -1) {
            estimatedRevenueIndex = headers.findIndex(h => h === 'estimated_revenue')
          }
          if (estimatedRevenueIndex === -1) {
            estimatedRevenueIndex = headers.findIndex(h => h?.toLowerCase().includes('estimated') && h?.toLowerCase().includes('revenue'))
          }
          if (estimatedRevenueIndex === -1) {
            estimatedRevenueIndex = headers.findIndex(h => h?.toLowerCase().includes('estimated'))
          }
          
          let finalRevenueIndex = headers.findIndex(h => h === 'Final realised revenue')
          if (finalRevenueIndex === -1) {
            finalRevenueIndex = headers.findIndex(h => h === 'realised_revenue')
          }
          if (finalRevenueIndex === -1) {
            finalRevenueIndex = headers.findIndex(h => h === 'final_revenue')
          }
          if (finalRevenueIndex === -1) {
            finalRevenueIndex = headers.findIndex(h => h?.toLowerCase().includes('final') && h?.toLowerCase().includes('revenue'))
          }
          if (finalRevenueIndex === -1) {
            finalRevenueIndex = headers.findIndex(h => h?.toLowerCase().includes('final'))
          }
          
          let groupCountIndex = headers.findIndex(h => h === 'Nb of nights')
          if (groupCountIndex === -1) {
            groupCountIndex = headers.findIndex(h => h === 'group_count')
          }
          if (groupCountIndex === -1) {
            groupCountIndex = headers.findIndex(h => h?.toLowerCase().includes('group') && h?.toLowerCase().includes('count'))
          }
          if (groupCountIndex === -1) {
            groupCountIndex = headers.findIndex(h => h?.toLowerCase().includes('group'))
          }
          
          let managerIndex = headers.findIndex(h => h === 'Manager')
          if (managerIndex === -1) {
            managerIndex = headers.findIndex(h => h === 'request_manager')
          }
          if (managerIndex === -1) {
            managerIndex = headers.findIndex(h => h?.toLowerCase().includes('manager'))
          }
          
          let companyIndex = headers.findIndex(h => h === 'Company')
          if (companyIndex === -1) {
            companyIndex = headers.findIndex(h => h === 'company')
          }
          if (companyIndex === -1) {
            companyIndex = headers.findIndex(h => h?.toLowerCase().includes('company'))
          }
          
          // Additional column mappings
          let segmentIndex = headers.findIndex(h => h === 'Segment')
          if (segmentIndex === -1) {
            segmentIndex = headers.findIndex(h => h === 'segment')
          }
          if (segmentIndex === -1) {
            segmentIndex = headers.findIndex(h => h?.toLowerCase().includes('segment'))
          }
          
          let checkInIndex = headers.findIndex(h => h === 'Check in')
          if (checkInIndex === -1) {
            checkInIndex = headers.findIndex(h => h === 'check_in')
          }
          if (checkInIndex === -1) {
            checkInIndex = headers.findIndex(h => h?.toLowerCase().includes('check') && h?.toLowerCase().includes('in'))
          }
          
          let venueIndex = headers.findIndex(h => h === 'Venue')
          if (venueIndex === -1) {
            venueIndex = headers.findIndex(h => h === 'venue')
          }
          if (venueIndex === -1) {
            venueIndex = headers.findIndex(h => h?.toLowerCase().includes('venue'))
          }
          
          let clientRequestRefIndex = headers.findIndex(h => h === 'Client request reference')
          if (clientRequestRefIndex === -1) {
            clientRequestRefIndex = headers.findIndex(h => h === 'client_request_reference')
          }
          if (clientRequestRefIndex === -1) {
            clientRequestRefIndex = headers.findIndex(h => h?.toLowerCase().includes('client') && h?.toLowerCase().includes('reference'))
          }
          
          // Debug: Log found indices
          console.log('Column indices found:', {
            requestIdIndex,
            statusIndex,
            estimatedRevenueIndex,
            finalRevenueIndex,
            groupCountIndex,
            managerIndex,
            companyIndex,
            segmentIndex,
            checkInIndex,
            venueIndex,
            clientRequestRefIndex
          })
          
          // Validate that required columns were found
          if (requestIdIndex === -1) {
            console.error('Available headers:', headers)
            console.error('Request ID index not found. Tried to find: Id, request_id, or any column containing "id"')
            throw new Error('Request ID column not found in Excel file. Available headers: ' + headers.join(', '))
          }
          
          // Process data rows
          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i]
            if (row && row.length > 0) {
              const rawRequestId = row[requestIdIndex]
              
              // More robust request ID parsing
              let requestId: number
              if (typeof rawRequestId === 'number') {
                requestId = rawRequestId
              } else if (typeof rawRequestId === 'string') {
                // Remove any non-numeric characters and parse
                const cleanId = rawRequestId.replace(/[^0-9]/g, '')
                requestId = parseInt(cleanId) || 0
              } else {
                requestId = parseInt(rawRequestId?.toString() || '0') || 0
              }
              
              const excelRow: ExcelRow = {
                request_id: requestId,
                status: row[statusIndex]?.toString() || '',
                estimated_revenue: parseFloat(row[estimatedRevenueIndex]?.toString() || '0') || 0,
                final_revenue: parseFloat(row[finalRevenueIndex]?.toString() || '0') || 0,
                group_count: parseInt(row[groupCountIndex]?.toString() || '0') || 0,
                manager: row[managerIndex]?.toString() || '',
                company: row[companyIndex]?.toString() || '',
                segment: row[segmentIndex]?.toString() || '',
                check_in: row[checkInIndex]?.toString() || '',
                venue: (row[venueIndex]?.toString() || '').trim(),
                client_request_reference: row[clientRequestRefIndex]?.toString() || ''
              }
              
              // Debug: Log first few rows to see what's being parsed
              if (i <= 5) {
                console.log(`Row ${i}:`, {
                  rawRequestId,
                  rawRequestIdType: typeof rawRequestId,
                  parsedRequestId: requestId,
                  excelRow
                })
              }
              
              // Only add rows with valid request_id
              if (excelRow.request_id > 0) {
                rows.push(excelRow)
              }
            }
          }
          
          console.log('Total rows parsed:', rows.length)
          console.log('Sample parsed rows:', rows.slice(0, 5))
          
          resolve(rows)
        } catch (error) {
          console.error('Excel parsing error:', error)
          reject(error)
        }
      }
      reader.onerror = reject
      reader.readAsArrayBuffer(file)
    })
  }

  // Compare Excel data with view data
  const compareData = async (excelRows: ExcelRow[]) => {
    setIsComparing(true)
    try {
      // Fetch current view data based on filters
      const viewData = await fetchViewData()
      
      console.log('Excel records:', excelRows.length)
      console.log('View records:', viewData.length)
      
      const result: ComparisonResult = {
        missingInView: [],
        missingInFile: [],
        statusDiscrepancies: [],
        groupCountDiscrepancies: [],
        estimatedRevenueDiscrepancies: [],
        finalRevenueDiscrepancies: [],
        viewRecordCount: viewData.length,
        totalEstimatedRevenueFile: 0,
        totalEstimatedRevenueView: 0,
        totalFinalRevenueFile: 0,
        totalFinalRevenueView: 0
      }

      // Calculate total revenues
      result.totalEstimatedRevenueFile = excelRows.reduce((sum, row) => sum + (row.estimated_revenue || 0), 0)
      result.totalEstimatedRevenueView = viewData.reduce((sum, row) => sum + (row.estimated_revenue || 0), 0)
      result.totalFinalRevenueFile = excelRows.reduce((sum, row) => sum + (row.final_revenue || 0), 0)
      result.totalFinalRevenueView = viewData.reduce((sum, row) => sum + (row.final_revenue || 0), 0)

      // Create sets of request IDs for efficient lookup (normalize to strings for comparison)
      const excelRequestIds = new Set(excelRows.map(row => String(row.request_id)))
      const viewRequestIds = new Set(viewData.map(row => String(row.request_id)))

      // Debug: Log sample request IDs from both sources
      console.log('Sample Excel request IDs:', Array.from(excelRequestIds).slice(0, 10))
      console.log('Sample View request IDs:', Array.from(viewRequestIds).slice(0, 10))
      console.log('Excel request ID types:', excelRows.slice(0, 5).map(row => ({ id: row.request_id, type: typeof row.request_id })))
      console.log('View request ID types:', viewData.slice(0, 5).map(row => ({ id: row.request_id, type: typeof row.request_id })))

      // Debug: Check specific request_id 949312
      const excelRow949312 = excelRows.find(row => String(row.request_id) === '949312')
      const viewRow949312 = viewData.find(row => String(row.request_id) === '949312')
      console.log('Excel row 949312:', excelRow949312)
      console.log('View row 949312:', viewRow949312)
      console.log('Is 949312 in Excel set?', excelRequestIds.has('949312'))
      console.log('Is 949312 in View set?', viewRequestIds.has('949312'))
      
      // Debug: Check for type mismatches
      const excelRow949312Str = excelRows.find(row => String(row.request_id) === '949312')
      const viewRow949312Str = viewData.find(row => String(row.request_id) === '949312')
      console.log('Excel row 949312 (string):', excelRow949312Str)
      console.log('View row 949312 (string):', viewRow949312Str)
      
      // Debug: Check all request IDs that contain 949312
      const excelRowsWith949312 = excelRows.filter(row => String(row.request_id).includes('949312'))
      const viewRowsWith949312 = viewData.filter(row => String(row.request_id).includes('949312'))
      console.log('All Excel rows containing 949312:', excelRowsWith949312)
      console.log('All View rows containing 949312:', viewRowsWith949312)
      
      // Debug: Check if 949312 is in the sets
      console.log('Excel set contains 949312?', excelRequestIds.has('949312'))
      console.log('View set contains 949312?', viewRequestIds.has('949312'))
      console.log('Excel set size:', excelRequestIds.size)
      console.log('View set size:', viewRequestIds.size)
      
      // Debug: Check for similar request IDs
      const similarExcelIds = Array.from(excelRequestIds).filter(id => id.includes('94931'))
      const similarViewIds = Array.from(viewRequestIds).filter(id => id.includes('94931'))
      console.log('Excel IDs containing 94931:', similarExcelIds)
      console.log('View IDs containing 94931:', similarViewIds)
      
      // Debug: Check the actual data that will be marked as missing
      const missingInViewSample = excelRows.filter(row => !viewRequestIds.has(String(row.request_id))).slice(0, 5)
      console.log('Sample missing in view (first 5):', missingInViewSample.map(row => ({ 
        request_id: row.request_id, 
        request_id_str: String(row.request_id),
        company: row.company 
      })))
      
      // Debug: Check if 949312 is in the missing list
      const is949312Missing = excelRows.find(row => String(row.request_id) === '949312' && !viewRequestIds.has(String(row.request_id)))
      console.log('Is 949312 in missing list?', !!is949312Missing)

      // Find missing requests (using string comparison)
      excelRows.forEach(excelRow => {
        if (!viewRequestIds.has(String(excelRow.request_id))) {
          result.missingInView.push(excelRow)
        }
      })

      viewData.forEach(viewRow => {
        if (!excelRequestIds.has(String(viewRow.request_id))) {
          result.missingInFile.push(viewRow)
        }
      })

                // Helper function to normalize status for comparison
          const normalizeStatus = (status: string): string => {
            const statusMap: { [key: string]: string } = {
              'Reserved': 'Booked',
              'Open': 'Open',
              'Lost': 'Lost'
            }
            return statusMap[status] || status
          }

          // Group Excel rows by request_id to handle multiple rows per request
          const excelRowsByRequestId = new Map<string, ExcelRow[]>()
          excelRows.forEach(excelRow => {
            const requestId = String(excelRow.request_id)
            if (!excelRowsByRequestId.has(requestId)) {
              excelRowsByRequestId.set(requestId, [])
            }
            excelRowsByRequestId.get(requestId)!.push(excelRow)
          })

          // Group view rows by request_id
          const viewRowsByRequestId = new Map<string, any[]>()
          viewData.forEach(viewRow => {
            const requestId = String(viewRow.request_id)
            if (!viewRowsByRequestId.has(requestId)) {
              viewRowsByRequestId.set(requestId, [])
            }
            viewRowsByRequestId.get(requestId)!.push(viewRow)
          })

          // Find discrepancies for matching request IDs
          excelRowsByRequestId.forEach((excelRowsForRequest, requestId) => {
            const matchingViewRows = viewRowsByRequestId.get(requestId) || []
            
            if (matchingViewRows.length > 0) {
              // Group count comparison - count actual groups per request_id
              const excelGroupsForRequest = excelRowsForRequest.length
              const viewGroupsForRequest = matchingViewRows.length
              
              if (excelGroupsForRequest !== viewGroupsForRequest) {
                result.groupCountDiscrepancies.push({
                  request_id: parseInt(requestId),
                  fileCount: excelGroupsForRequest,
                  viewCount: viewGroupsForRequest
                })
              }

              // Create a copy of view rows to track which ones have been matched
              const availableViewRows = [...matchingViewRows]
              
              // For each Excel row, find the best matching view row
              excelRowsForRequest.forEach(excelRow => {
                const normalizedExcelStatus = normalizeStatus(excelRow.status)
                
                // Try to find the best match by final_revenue first (most specific)
                let bestMatch = availableViewRows.find(viewRow => 
                  Math.abs(viewRow.final_revenue - excelRow.final_revenue) < 0.01
                )
                
                // If no match by final_revenue, try estimated_revenue + status
                if (!bestMatch) {
                  bestMatch = availableViewRows.find(viewRow => 
                    normalizeStatus(viewRow.status) === normalizedExcelStatus &&
                    Math.abs(viewRow.estimated_revenue - excelRow.estimated_revenue) < 0.01
                  )
                }
                
                // If still no match, try just by status
                if (!bestMatch) {
                  bestMatch = availableViewRows.find(viewRow => 
                    normalizeStatus(viewRow.status) === normalizedExcelStatus
                  )
                }
                
                // Fallback to first available view row
                if (!bestMatch) {
                  bestMatch = availableViewRows[0]
                }
                
                // Remove the matched view row to avoid duplicate matches
                if (bestMatch) {
                  const matchIndex = availableViewRows.indexOf(bestMatch)
                  if (matchIndex > -1) {
                    availableViewRows.splice(matchIndex, 1)
                  }
                }

                // Status comparison using normalized status
                if (normalizeStatus(excelRow.status) !== normalizeStatus(bestMatch.status)) {
                  result.statusDiscrepancies.push({
                    request_id: excelRow.request_id,
                    fileStatus: excelRow.status,
                    viewStatus: bestMatch.status
                  })
                }

                // Revenue comparisons
                if (Math.abs(excelRow.estimated_revenue - bestMatch.estimated_revenue) > 0.01) {
                  result.estimatedRevenueDiscrepancies.push({
                    request_id: excelRow.request_id,
                    fileRevenue: excelRow.estimated_revenue,
                    viewRevenue: bestMatch.estimated_revenue
                  })
                }

                if (Math.abs(excelRow.final_revenue - bestMatch.final_revenue) > 0.01) {
                  result.finalRevenueDiscrepancies.push({
                    request_id: excelRow.request_id,
                    fileRevenue: excelRow.final_revenue,
                    viewRevenue: bestMatch.final_revenue
                  })
                }
              })
            }
          })

      console.log('Comparison results:', {
        missingInView: result.missingInView.length,
        missingInFile: result.missingInFile.length,
        statusDiscrepancies: result.statusDiscrepancies.length,
        groupCountDiscrepancies: result.groupCountDiscrepancies.length,
        estimatedRevenueDiscrepancies: result.estimatedRevenueDiscrepancies.length,
        finalRevenueDiscrepancies: result.finalRevenueDiscrepancies.length
      })

      setComparisonResult(result)
    } catch (error) {
      console.error('Comparison failed:', error)
    } finally {
      setIsComparing(false)
    }
  }

  // Fetch view data based on current filters
  const fetchViewData = async (): Promise<any[]> => {
    try {
      const { createClient } = await import('@supabase/supabase-js')
      const supabase = createClient(
        import.meta.env.VITE_SUPABASE_URL,
        import.meta.env.VITE_SUPABASE_ANON_KEY
      )

      let query = supabase
        .from('mv_group_enriched_summary')
        .select('request_id, status, estimated_revenue, realised_revenue, request_manager, company, venue, hotel_group_name, segment')
        .neq('status', 'Not launched') // Add implicit filter for "Not Launched" status

      // Apply filters
      if (!isAllSelected(filters.selectedCreationYears, availableOptions.availableCreationYears)) {
        const years = Array.from(filters.selectedCreationYears).sort()
        query = query.in('request_year', years)
      }

      if (!isAllSelected(filters.selectedArrivalYears, availableOptions.availableArrivalYears)) {
        const years = Array.from(filters.selectedArrivalYears).sort()
        query = query.in('arrival_year', years)
      }

      if (!isAllSelected(filters.selectedManagers, availableOptions.availableManagers)) {
        const managers = Array.from(filters.selectedManagers).sort()
        query = query.in('request_manager', managers)
      }

      if (!isAllSelected(filters.selectedSegments, availableOptions.availableSegments)) {
        const segments = Array.from(filters.selectedSegments).sort()
        query = query.in('segment', segments)
      }

      if (!filters.allClientsSelected && filters.selectedClients.size > 0) {
        const clients = Array.from(filters.selectedClients).sort()
        query = query.in('company', clients)
      }

      if (!filters.allHotelsSelected && filters.selectedHotels.size > 0) {
        const hotels = Array.from(filters.selectedHotels).sort()
        query = query.in('venue', hotels)
      }

      // Hotel group filter removed for debugging

      if (!isAllSelected(filters.selectedArrivalMonths, [1,2,3,4,5,6,7,8,9,10,11,12])) {
        const months = Array.from(filters.selectedArrivalMonths).sort()
        query = query.in('arrival_month', months)
      }

      if (!isAllSelected(filters.selectedArrivalQuarters, [1,2,3,4])) {
        const quarters = Array.from(filters.selectedArrivalQuarters).sort()
        query = query.in('arrival_quarter', quarters)
      }

      if (!isAllSelected(filters.selectedCreationMonths, [1,2,3,4,5,6,7,8,9,10,11,12])) {
        const months = Array.from(filters.selectedCreationMonths).sort()
        query = query.in('request_month', months)
      }

      if (!isAllSelected(filters.selectedCreationQuarters, [1,2,3,4])) {
        const quarters = Array.from(filters.selectedCreationQuarters).sort()
        query = query.in('request_quarter', quarters)
      }

      console.log('Final query filters applied')
      console.log('Current filters being applied:')
      console.log('- Creation Years:', Array.from(filters.selectedCreationYears))
      console.log('- Arrival Years:', Array.from(filters.selectedArrivalYears))
      console.log('- Managers:', Array.from(filters.selectedManagers))
      console.log('- Segments:', Array.from(filters.selectedSegments))
      console.log('- Clients:', Array.from(filters.selectedClients))
      console.log('- Hotels:', Array.from(filters.selectedHotels))
      console.log('- Creation Months:', Array.from(filters.selectedCreationMonths))
      console.log('- Creation Quarters:', Array.from(filters.selectedCreationQuarters))
      console.log('- Arrival Months:', Array.from(filters.selectedArrivalMonths))
      console.log('- Arrival Quarters:', Array.from(filters.selectedArrivalQuarters))
      console.log('- All Clients Selected:', filters.allClientsSelected)
      console.log('- All Hotels Selected:', filters.allHotelsSelected)
      console.log('- All Hotel Groups Selected:', filters.allHotelGroupsSelected)
      
      // Fetch all data with pagination to handle large datasets
      const allData: any[] = []
      let page = 0
      const pageSize = 1000
      let hasMore = true

      while (hasMore) {
        // Clone the query for each page to avoid query state issues
        let pageQuery = supabase
          .from('mv_group_enriched_summary')
          .select('request_id, status, estimated_revenue, realised_revenue, request_manager, company, venue, hotel_group_name, segment')
          .neq('status', 'Not launched')

        // Reapply all filters for each page
        if (!isAllSelected(filters.selectedCreationYears, availableOptions.availableCreationYears)) {
          const years = Array.from(filters.selectedCreationYears).sort()
          pageQuery = pageQuery.in('request_year', years)
        }

        if (!isAllSelected(filters.selectedArrivalYears, availableOptions.availableArrivalYears)) {
          const years = Array.from(filters.selectedArrivalYears).sort()
          pageQuery = pageQuery.in('arrival_year', years)
        }

        if (!isAllSelected(filters.selectedManagers, availableOptions.availableManagers)) {
          const managers = Array.from(filters.selectedManagers).sort()
          pageQuery = pageQuery.in('request_manager', managers)
        }

        if (!isAllSelected(filters.selectedSegments, availableOptions.availableSegments)) {
          const segments = Array.from(filters.selectedSegments).sort()
          pageQuery = pageQuery.in('segment', segments)
        }

        if (!filters.allClientsSelected && filters.selectedClients.size > 0) {
          const clients = Array.from(filters.selectedClients).sort()
          pageQuery = pageQuery.in('company', clients)
        }

        if (!filters.allHotelsSelected && filters.selectedHotels.size > 0) {
          const hotels = Array.from(filters.selectedHotels).sort()
          pageQuery = pageQuery.in('venue', hotels)
        }

        if (!isAllSelected(filters.selectedArrivalMonths, [1,2,3,4,5,6,7,8,9,10,11,12])) {
          const months = Array.from(filters.selectedArrivalMonths).sort()
          pageQuery = pageQuery.in('arrival_month', months)
        }

        if (!isAllSelected(filters.selectedArrivalQuarters, [1,2,3,4])) {
          const quarters = Array.from(filters.selectedArrivalQuarters).sort()
          pageQuery = pageQuery.in('arrival_quarter', quarters)
        }

        if (!isAllSelected(filters.selectedCreationMonths, [1,2,3,4,5,6,7,8,9,10,11,12])) {
          const months = Array.from(filters.selectedCreationMonths).sort()
          pageQuery = pageQuery.in('request_month', months)
        }

        if (!isAllSelected(filters.selectedCreationQuarters, [1,2,3,4])) {
          const quarters = Array.from(filters.selectedCreationQuarters).sort()
          pageQuery = pageQuery.in('request_quarter', quarters)
        }

        const { data, error } = await pageQuery
          .range(page * pageSize, (page + 1) * pageSize - 1)

        if (error) {
          console.error('Error fetching view data:', error)
          console.error('Query details:', pageQuery)
          return []
        }

        if (data && data.length > 0) {
          allData.push(...data)
          page++
          // If we got less than pageSize records, we've reached the end
          hasMore = data.length === pageSize
        } else {
          hasMore = false
        }
      }

      console.log(`Fetched ${allData.length} total records across ${page} pages`)
      
      // Debug: Check if 949312 is in the raw fetched data
      const raw949312 = allData.find(row => String(row.request_id) === '949312')
      console.log('Raw fetched data contains 949312?', !!raw949312)
      if (raw949312) {
        console.log('Raw 949312 data:', raw949312)
      }
      
      // Debug: Direct query for request 949312 to see if it exists with current filters
      try {
        const directQuery = supabase
          .from('mv_group_enriched_summary')
          .select('request_id, status, estimated_revenue, realised_revenue, request_manager, company, venue, hotel_group_name, segment')
          .eq('request_id', 949312)
          .neq('status', 'Not launched')
        
        // Apply the same filters as the main query
        if (!isAllSelected(filters.selectedCreationYears, availableOptions.availableCreationYears)) {
          const years = Array.from(filters.selectedCreationYears).sort()
          directQuery.in('request_year', years)
        }
        if (!isAllSelected(filters.selectedArrivalYears, availableOptions.availableArrivalYears)) {
          const years = Array.from(filters.selectedArrivalYears).sort()
          directQuery.in('arrival_year', years)
        }
        if (!isAllSelected(filters.selectedManagers, availableOptions.availableManagers)) {
          const managers = Array.from(filters.selectedManagers).sort()
          directQuery.in('request_manager', managers)
        }
        if (!isAllSelected(filters.selectedSegments, availableOptions.availableSegments)) {
          const segments = Array.from(filters.selectedSegments).sort()
          directQuery.in('segment', segments)
        }
        if (!filters.allClientsSelected && filters.selectedClients.size > 0) {
          const clients = Array.from(filters.selectedClients).sort()
          directQuery.in('company', clients)
        }
        if (!filters.allHotelsSelected && filters.selectedHotels.size > 0) {
          const hotels = Array.from(filters.selectedHotels).sort()
          directQuery.in('venue', hotels)
        }
        
        const { data: directData, error: directError } = await directQuery
        console.log('Direct query for 949312 result:', directData)
        console.log('Direct query error:', directError)
        
        // Also try without the status filter to see if that's the issue
        const directQueryNoStatus = supabase
          .from('mv_group_enriched_summary')
          .select('request_id, status, estimated_revenue, realised_revenue, request_manager, company, venue, hotel_group_name, segment')
          .eq('request_id', 949312)
        
        const { data: directDataNoStatus, error: directErrorNoStatus } = await directQueryNoStatus
        console.log('Direct query for 949312 (no status filter) result:', directDataNoStatus)
        console.log('Direct query (no status filter) error:', directErrorNoStatus)
      } catch (error) {
        console.log('Direct query failed:', error)
      }

      // Return individual group records (not aggregated)
      return allData.map(row => ({
        request_id: typeof row.request_id === 'string' ? parseInt(row.request_id) : row.request_id,
        status: row.status,
        estimated_revenue: row.estimated_revenue || 0,
        realised_revenue: row.realised_revenue || 0,
        final_revenue: row.realised_revenue || 0, // Map realised_revenue to final_revenue for comparison
        group_count: 1, // Each row represents one group
        manager: row.request_manager,
        company: row.company,
        venue: row.venue?.trim() || ''
      }))
    } catch (error) {
      console.error('Error fetching view data:', error)
      return []
    }
  }

  // Handle file upload
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      try {
        console.log('Uploading file:', file.name, 'Size:', file.size, 'Type:', file.type)
        const rows = await parseExcelFile(file)
        setExcelData(rows)
        
        if (rows.length > 0) {
          await compareData(rows)
          setActiveTab('comparison')
        } else {
          alert('No valid data found in Excel file. Please check the file content.')
        }
      } catch (error) {
        console.error('File upload error:', error)
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
        alert(`Failed to parse Excel file: ${errorMessage}`)
      }
    }
  }

  // Helper function to check if all options are selected (same logic as dashboard)
  const isAllSelected = (selected: Set<any>, allOptions: any[]) => {
    // When "All" is selected, the set contains all values, but we should treat it as no filter
    // This matches the dashboard logic where "All" means no filter applied
    return selected.size === 0 || selected.size === allOptions.length
  }

  // Generate SQL query based on filters
  const generateSQLQuery = () => {
    const conditions: string[] = []
    
    // Add implicit filter for "Not Launched" status (excluded from mv_group_enriched_summary)
    conditions.push(`status IS DISTINCT FROM 'Not launched'`)
    
    // Add filter conditions
    if (!isAllSelected(filters.selectedCreationYears, availableOptions.availableCreationYears)) {
      const years = Array.from(filters.selectedCreationYears).sort()
      conditions.push(`request_year IN (${years.join(', ')})`)
    }
    
    if (!isAllSelected(filters.selectedArrivalYears, availableOptions.availableArrivalYears)) {
      const years = Array.from(filters.selectedArrivalYears).sort()
      conditions.push(`arrival_year IN (${years.join(', ')})`)
    }
    
    if (!isAllSelected(filters.selectedManagers, availableOptions.availableManagers)) {
      const managers = Array.from(filters.selectedManagers).filter(m => m != null).map(m => `'${m.replace(/'/g, "''")}'`).sort()
      conditions.push(`request_manager IN (${managers.join(', ')})`)
    }
    
    if (!isAllSelected(filters.selectedSegments, availableOptions.availableSegments)) {
      const segments = Array.from(filters.selectedSegments).filter(s => s != null).map(s => `'${s.replace(/'/g, "''")}'`).sort()
      conditions.push(`segment IN (${segments.join(', ')})`)
    }
    
    if (!filters.allClientsSelected && filters.selectedClients.size > 0) {
      const clients = Array.from(filters.selectedClients).filter(c => c != null).map(c => `'${c.replace(/'/g, "''")}'`).sort()
      conditions.push(`company IN (${clients.join(', ')})`)
    }
    
    if (!filters.allHotelsSelected && filters.selectedHotels.size > 0) {
      const hotels = Array.from(filters.selectedHotels).filter(h => h != null).map(h => `'${h.replace(/'/g, "''")}'`).sort()
      conditions.push(`venue IN (${hotels.join(', ')})`)
    }
    
    // Hotel group filter removed for debugging
    
    if (!isAllSelected(filters.selectedArrivalMonths, [1,2,3,4,5,6,7,8,9,10,11,12])) {
      const months = Array.from(filters.selectedArrivalMonths).sort()
      conditions.push(`arrival_month IN (${months.join(', ')})`)
    }
    
    if (!isAllSelected(filters.selectedArrivalQuarters, [1,2,3,4])) {
      const quarters = Array.from(filters.selectedArrivalQuarters).sort()
      conditions.push(`arrival_quarter IN (${quarters.join(', ')})`)
    }
    
    if (!isAllSelected(filters.selectedCreationMonths, [1,2,3,4,5,6,7,8,9,10,11,12])) {
      const months = Array.from(filters.selectedCreationMonths).sort()
      conditions.push(`request_month IN (${months.join(', ')})`)
    }
    
    if (!isAllSelected(filters.selectedCreationQuarters, [1,2,3,4])) {
      const quarters = Array.from(filters.selectedCreationQuarters).sort()
      conditions.push(`request_quarter IN (${quarters.join(', ')})`)
    }
    
    const whereClause = conditions.length > 0 ? `WHERE ${conditions.join(' AND ')}` : ''
    
    const groupedQuery = `SELECT 
  request_manager,
  COUNT(DISTINCT request_id) as nb_projects,
  COUNT(CASE WHEN status = 'Lost' THEN 1 END) as lost_groups,
  COUNT(CASE WHEN status = 'Booked' THEN 1 END) as booked_groups,
  COUNT(CASE WHEN status = 'Open' THEN 1 END) as open_groups,
  SUM(estimated_revenue) as total_estimated_revenue,
  SUM(realised_revenue) as total_realised_revenue
FROM mv_group_enriched_summary
${whereClause}
GROUP BY request_manager
ORDER BY total_realised_revenue DESC;`

    const detailedQuery = `SELECT 
  request_id,
  request_manager,
  company,
  venue,
  hotel_group_name,
  segment,
  status,
  estimated_revenue,
  realised_revenue,
  arrival_year,
  arrival_month,
  arrival_quarter,
  request_year,
  request_month,
  request_quarter
FROM mv_group_enriched_summary
${whereClause}
ORDER BY request_manager, request_id;`
    
    return { groupedQuery, detailedQuery }
  }

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900 mb-4">
                      Data Check - Current Filters
                    </Dialog.Title>
                    
                    {/* Tab Navigation */}
                    <div className="border-b border-gray-200 mb-4">
                      <nav className="-mb-px flex space-x-8">
                        <button
                          onClick={() => setActiveTab('filters')}
                          className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'filters'
                              ? 'border-blue-500 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          Filters & SQL
                        </button>
                        <button
                          onClick={() => setActiveTab('comparison')}
                          className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'comparison'
                              ? 'border-blue-500 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          Excel Comparison
                        </button>
                      </nav>
                    </div>

                    {activeTab === 'filters' && (
                      <div className="space-y-6">
                        {/* Filters Section */}
                        <div>
                          <h4 className="font-medium text-gray-900 mb-3">Applied Filters</h4>
                          <div className="bg-gray-50 p-4 rounded-md space-y-2 text-sm h-48 overflow-y-auto">
                            <div><span className="font-medium">Status:</span> <span className="text-gray-600">Not Launched (excluded)</span></div>
                            <div><span className="font-medium">Creation Years:</span> {formatFilterValues(filters.selectedCreationYears, availableOptions.availableCreationYears)}</div>
                            <div><span className="font-medium">Arrival Years:</span> {formatFilterValues(filters.selectedArrivalYears, availableOptions.availableArrivalYears)}</div>
                            <div><span className="font-medium">Managers:</span> {formatFilterValues(filters.selectedManagers, availableOptions.availableManagers)}</div>
                            <div><span className="font-medium">Segments:</span> {formatFilterValues(filters.selectedSegments, availableOptions.availableSegments)}</div>
                            <div><span className="font-medium">Clients:</span> {formatFilterValues(filters.selectedClients, availableOptions.clients, filters.allClientsSelected)}</div>
                            <div><span className="font-medium">Hotels:</span> {formatFilterValues(filters.selectedHotels, availableOptions.hotels, filters.allHotelsSelected)}</div>
                            <div><span className="font-medium">Hotel Groups:</span> {formatFilterValues(filters.selectedHotelGroups, availableOptions.hotelGroups, filters.allHotelGroupsSelected)}</div>
                            <div><span className="font-medium">Arrival Months:</span> {formatFilterValues(filters.selectedArrivalMonths, [1,2,3,4,5,6,7,8,9,10,11,12])}</div>
                            <div><span className="font-medium">Arrival Quarters:</span> {formatFilterValues(filters.selectedArrivalQuarters, [1,2,3,4])}</div>
                            <div><span className="font-medium">Creation Months:</span> {formatFilterValues(filters.selectedCreationMonths, [1,2,3,4,5,6,7,8,9,10,11,12])}</div>
                            <div><span className="font-medium">Creation Quarters:</span> {formatFilterValues(filters.selectedCreationQuarters, [1,2,3,4])}</div>
                          </div>
                        </div>
                        
                        {/* Grouped Query Section */}
                        <div>
                          <div className="flex justify-between items-center mb-3">
                            <h4 className="font-medium text-gray-900">Grouped by Manager Query</h4>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => copyToClipboard(generateSQLQuery().groupedQuery, 'grouped')}
                                className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                                title="Copy query"
                              >
                                <Copy className="h-4 w-4" />
                              </button>
                              {copySuccess === 'grouped' && (
                                <span className="text-xs text-green-600 font-medium">Copied!</span>
                              )}
                            </div>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-md h-48 overflow-y-auto">
                            <pre className="text-xs text-gray-700 whitespace-pre-wrap overflow-x-auto">
                              {generateSQLQuery().groupedQuery}
                            </pre>
                          </div>
                        </div>
                        
                        {/* Detailed Query Section */}
                        <div>
                          <div className="flex justify-between items-center mb-3">
                            <h4 className="font-medium text-gray-900">Detailed List Query</h4>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => copyToClipboard(generateSQLQuery().detailedQuery, 'detailed')}
                                className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                                title="Copy query"
                              >
                                <Copy className="h-4 w-4" />
                              </button>
                              {copySuccess === 'detailed' && (
                                <span className="text-xs text-green-600 font-medium">Copied!</span>
                              )}
                            </div>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-md h-48 overflow-y-auto">
                            <pre className="text-xs text-gray-700 whitespace-pre-wrap overflow-x-auto">
                              {generateSQLQuery().detailedQuery}
                            </pre>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTab === 'comparison' && (
                      <div className="space-y-6">
                        {/* File Upload Section */}
                        <div>
                          <div className="flex justify-between items-center mb-3">
                            <h4 className="font-medium text-gray-900">Upload Excel File</h4>
                            {(excelData.length > 0 || comparisonResult) && (
                              <button
                                type="button"
                                onClick={clearComparison}
                                className="px-3 py-1 text-sm text-red-600 hover:text-red-800 border border-red-300 hover:border-red-400 rounded-md transition-colors"
                              >
                                Clear Comparison
                              </button>
                            )}
                          </div>
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                            <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <div className="mt-4">
                              <label htmlFor="file-upload" className="cursor-pointer">
                                <span className="mt-2 block text-sm font-medium text-gray-900">
                                  Upload Excel file from Backyou system
                                </span>
                                <span className="mt-1 block text-xs text-gray-500">
                                  CSV or Excel format with columns: request_id, status, estimated_revenue, final_revenue, group_count
                                </span>
                              </label>
                              <input
                                id="file-upload"
                                name="file-upload"
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                className="sr-only"
                                onChange={handleFileUpload}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Comparison Results */}
                        {isComparing && (
                          <div className="text-center py-4">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="mt-2 text-sm text-gray-600">Comparing data...</p>
                          </div>
                        )}

                        {comparisonResult && (
                          <div className="space-y-4">
                            <h4 className="font-medium text-gray-900">Comparison Results</h4>
                            
                            {/* Summary */}
                            <div className="bg-gray-50 p-4 rounded-md">
                              <h5 className="font-medium text-gray-800">
                                Total Discrepancies: {
                                  comparisonResult.missingInView.length +
                                  comparisonResult.missingInFile.length +
                                  comparisonResult.statusDiscrepancies.length +
                                  comparisonResult.groupCountDiscrepancies.length +
                                  comparisonResult.estimatedRevenueDiscrepancies.length +
                                  comparisonResult.finalRevenueDiscrepancies.length
                                }
                              </h5>
                            </div>

                            {/* Group Count Discrepancies */}
                            <div className="bg-purple-50 p-4 rounded-md">
                              <h5 className="font-medium text-purple-800 mb-2">
                                Group Count Discrepancies ({comparisonResult.groupCountDiscrepancies.length})
                              </h5>
                              <div className="text-sm text-purple-700 mb-2">
                                <div className="space-y-1">
                                  <div>Excel Groups: {excelData.length}</div>
                                  <div>View Groups: {comparisonResult.viewRecordCount}</div>
                                </div>
                              </div>
                              {comparisonResult.groupCountDiscrepancies.length > 0 ? (
                                <div className="text-sm text-purple-700 max-h-32 overflow-y-auto">
                                  {comparisonResult.groupCountDiscrepancies.map((item, index) => (
                                    <div key={`group-${item.request_id}-${index}`} className="flex justify-between">
                                      <span>Request {item.request_id}</span>
                                      <span>File: {item.fileCount} vs View: {item.viewCount}</span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-purple-600">No group count discrepancies found</p>
                              )}
                            </div>
                            
                            {/* Missing Requests */}
                            <div className="bg-red-50 p-4 rounded-md">
                              <h5 className="font-medium text-red-800 mb-2">
                                Missing in View ({comparisonResult.missingInView.length})
                              </h5>
                              {comparisonResult.missingInView.length > 0 ? (
                                <div className="text-sm text-red-700 max-h-32 overflow-y-auto">
                                                                {comparisonResult.missingInView.map((row, index) => (
                                <div key={`missing-view-${row.request_id}-${index}`} className="flex justify-between">
                                  <span>Request ID: {row.request_id}</span>
                                  <span className="text-gray-500">{row.company || row.manager}</span>
                                </div>
                              ))}
                                </div>
                              ) : (
                                <p className="text-sm text-red-600">No missing requests found</p>
                              )}
                            </div>

                            {/* Missing in File */}
                            <div className="bg-orange-50 p-4 rounded-md">
                              <h5 className="font-medium text-orange-800 mb-2">
                                Missing in Excel ({comparisonResult.missingInFile.length})
                              </h5>
                              {comparisonResult.missingInFile.length > 0 ? (
                                <div className="text-sm text-orange-700 max-h-32 overflow-y-auto">
                                                                {comparisonResult.missingInFile.map((row, index) => (
                                <div key={`missing-file-${row.request_id}-${index}`} className="flex justify-between">
                                  <span>Request ID: {row.request_id}</span>
                                  <span className="text-gray-500">{row.company || row.manager}</span>
                                </div>
                              ))}
                                </div>
                              ) : (
                                <p className="text-sm text-orange-600">No missing requests found</p>
                              )}
                            </div>

                            {/* Status Discrepancies */}
                            <div className="bg-yellow-50 p-4 rounded-md">
                              <h5 className="font-medium text-yellow-800 mb-2">
                                Status Discrepancies ({comparisonResult.statusDiscrepancies.length})
                              </h5>
                              {comparisonResult.statusDiscrepancies.length > 0 ? (
                                <div className="text-sm text-yellow-700 max-h-32 overflow-y-auto">
                                  {comparisonResult.statusDiscrepancies.map((item, index) => (
                                    <div key={`status-${item.request_id}-${index}`} className="flex justify-between">
                                      <span>Request {item.request_id}</span>
                                      <span>File: "{item.fileStatus}" vs View: "{item.viewStatus}"</span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-yellow-600">No status discrepancies found</p>
                              )}
                            </div>

                            {/* Revenue Discrepancies */}
                            <div className="bg-blue-50 p-4 rounded-md">
                              <h5 className="font-medium text-blue-800 mb-2">
                                Revenue Discrepancies
                              </h5>
                              <div className="space-y-2">
                                <div className="text-sm text-blue-700">
                                  Estimated Revenue: {comparisonResult.estimatedRevenueDiscrepancies.length} discrepancies
                                  {comparisonResult.estimatedRevenueDiscrepancies.length > 0 && (
                                    <>
                                      <span className="ml-2 font-medium">
                                        (Total: €{(comparisonResult.totalEstimatedRevenueView - comparisonResult.totalEstimatedRevenueFile).toLocaleString()})
                                      </span>
                                      <div className="mt-2 max-h-32 overflow-y-auto">
                                        {comparisonResult.estimatedRevenueDiscrepancies.map((item, index) => (
                                          <div key={`estimated-${item.request_id}-${index}`} className="flex justify-between text-xs">
                                            <span>Request {item.request_id}</span>
                                            <span>File: €{item.fileRevenue.toLocaleString()} vs View: €{item.viewRevenue.toLocaleString()}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </>
                                  )}
                                </div>
                                <div className="text-sm text-blue-700">
                                  Final Revenue: {comparisonResult.finalRevenueDiscrepancies.length} discrepancies
                                  {comparisonResult.finalRevenueDiscrepancies.length > 0 && (
                                    <>
                                      <span className="ml-2 font-medium">
                                        (Total: €{(comparisonResult.totalFinalRevenueView - comparisonResult.totalFinalRevenueFile).toLocaleString()})
                                      </span>
                                      <div className="mt-2 max-h-32 overflow-y-auto">
                                        {comparisonResult.finalRevenueDiscrepancies.map((item, index) => (
                                          <div key={`final-${item.request_id}-${index}`} className="flex justify-between text-xs">
                                            <span>Request {item.request_id}</span>
                                            <span>File: €{item.fileRevenue.toLocaleString()} vs View: €{item.viewRevenue.toLocaleString()}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto"
                    onClick={onClose}
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
} 